<template>
  <div
    tabindex="0"
    class="select-container no-focus-visible"
    :class="classList"
    @keydown.stop="$emit('handle-click-navigation', $event)"
  >
    <div
      v-if="label || $slots.label"
      class="select-container__label"
    >
      <label
        @click="onClick"
      >
        <template v-if="$slots.label">
          <slot name="label" />
        </template>
        <template v-else>
          {{ label }}
        </template>
      </label>
    </div>
    <div class="select-container__select">
      <BasePopover
        :visible="isActive"
        :padding="padding"
        :offset="offset"
        :options="popperOptions"
        :close-on-click-reference="false"
        :close-on-click-outside="false"
        :popper-class="[{'select-control__popper_in-modal': isInModal}, popperClass]"
        placement="bottom-start"
        :visible-arrow="visibleArrow"
        :gpu-acceleration="gpuAcceleration"
        @open="$emit('open')"
        @close="$emit('close')"
      >
        <template slot="reference">
          <div
            ref="trigger"
            class="select-control"
            @click="onClick"
          >
            <div class="select-control__content">
              <slot name="header" />
            </div>
            <div
              v-if="showDropdownIcon"
              class="select-control__icon"
              :class="{'select-control__icon_active': isActive}"
            >
              <MIcon :icon="arrowIcon" />
            </div>
          </div>
        </template>
        <template slot="popper">
          <BaseSelectDropdownOptions
            ref="dropdown"
            :dropdown-active="isActive"
            :width="computedDropdownWidth"
            :max-width-offset="maxWidthOffset"
            :small="small"
            :large="large"
            :class="selectDropdownClass"
            :lazy-render="lazyRender"
            :selected-item="selectedItem"
            :is-loading="isLoading"
            v-bind="$attrs"
          >
            <slot name="dropdown" />
          </BaseSelectDropdownOptions>
        </template>
      </BasePopover>
    </div>
  </div>
</template>


<script>
  import BasePopover from 'components/common/BasePopover';
  import BaseSelectDropdownOptions from 'components/Inputs/Select/common/BaseSelectDropdown/BaseSelectDropdownOptions';

  import bulletArrowIcon from 'assets/icons/bullet-arrow.svg';
  import MIcon from 'components/Design/MIcon';

  /**
   * @class BaseSelectDropdown
   * @emits 'trigger' - when dropdown starts opening or closing
   * @emits 'open' - when dropdown finished opening
   * @emits 'close' - when dropdown finished closing
   */

  export default {
    name: 'BaseSelectDropdown',
    components: {BasePopover, BaseSelectDropdownOptions, MIcon},
    inheritAttrs: false,
    model: {
      prop: 'active',
      event: 'trigger'
    },
    props: {
      active: { // is dropdown opened
        type: Boolean,
        default: false,
        required: true
      },
      placeholderOnlyName: {
        type: Boolean,
        default: false
      },
      disabled: Boolean,
      small: Boolean,
      large: Boolean,
      largeWithIcon: {
        type: Boolean,
        default: false
      },
      padding: {
        type: BasePopover.props.padding.type,
        default: BasePopover.props.padding.default
      },
      popperClass: {
        type: BasePopover.props.popperClass.type,
        default: BasePopover.props.popperClass.default
      },
      label: {
        type: String,
        default: ''
      },
      labelPosition: {
        type: String,
        default: 'top',
        choices: ['left', 'top']
      },
      offset: {
        type: BasePopover.props.offset.type,
        default: BasePopover.props.offset.default
      },
      popperOptions: {
        type: BasePopover.props.options.type,
        default: () => ({
          flip: {
            boundariesElement: 'scrollParent',
            behavior: ['bottom-end', 'bottom-start', 'top-end', 'top-start']
          }
        })
      },
      maxWidthOffset: {
        type: Number,
        default: 100
      },
      dropdownWidth: {
        type: Number,
        default: null
      },
      isInModal: { // hack for fix display select options above modal window
        type: Boolean,
        default: false
      },
      selectDropdownClass: {
        type: [String, Array],
        default: ''
      },
      showDropdownIcon: {
        type: Boolean,
        default: true
      },
      visibleArrow: {
        type: Boolean,
        default: false
      },
      closeOnClickOutside: {
        type: Boolean,
        default: true
      },
      gpuAcceleration: {
        type: Boolean,
        default: false
      },
      lazyRender: {
        type: Boolean,
        default: false
      },
      selectedItem: {
        type: Object,
        default: null
      },
      isLoading: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        arrowIcon: bulletArrowIcon,
        triggerWidth: null, // width of select input,
        // is needed to set width of trigger block in case its content is changing
        isActive: false // is dropdown opened
      };
    },
    computed: {
      classList () {
        return {
          'select-container_disabled': this.disabled,
          'select-container_small': this.small,
          'select-container_large': this.large,
          'select-container_large-with-icon': this.largeWithIcon,
          'select-container_with-bottom-text': this.selectedItem?.bottomBtnText,
          'select-container_large-placeholder-only-name': this.largeWithIcon && this.placeholderOnlyName,
          'select-container_label-top': this.labelPosition === 'top',
          'select-container_label-left': this.labelPosition === 'left'
        };
      },
      computedDropdownWidth () {
        if (this.dropdownWidth) {
          return this.dropdownWidth;
        }
        return this.triggerWidth;
      }
    },
    watch: {
      active (val) {
        if (!this.disabled) {
          this.triggerDropdown(val);
        }
      },
      isActive (val) {
        this.$emit('trigger', val);
      }
    },
    mounted () {
      this.updateTriggerWidth();
      window.addEventListener('resize', this.updateTriggerWidth);
    },
    destroyed () {

      // important to clean up!!
      document.removeEventListener('click', this.documentClick);
      window.removeEventListener('resize', this.updateTriggerWidth);
    },
    methods: {
      onClick (event) {
        // Propagate event to BasePopover onClick only when popover is closing by click on label
        if (this.isActive) {
          event.stopPropagation();
        }

        this.trigger(!this.isActive);
      },
      trigger (val) {
        if (this.disabled) {
          return;
        }
        this.isActive = val;
      },
      updateTriggerWidth () {
        const width = this.$refs.trigger.clientWidth;
        if (width) {
          this.triggerWidth = width;
        }
      },
      documentClick (e) {
        if (!this.closeOnClickOutside) {
          return;
        }
        const el = this.$children[0].$el;
        const target = e.target;
        if (document.body.contains(target) && el !== target && !el.contains(target)) {

          // check for click in dropdown
          const dropdownEl = this.$refs.dropdown.$el;
          if (dropdownEl !== target && !dropdownEl.contains(target)) {
            this.trigger(false);
          }
        }
      },
      showOptions () {
        document.addEventListener('click', this.documentClick);
        this.updateTriggerWidth();
      },
      hideOptions () {
        document.removeEventListener('click', this.documentClick);
      },
      triggerDropdown (val) {
        if (val) {
          this.isActive = val;
          this._$nextFrame(() => {
            this.showOptions();
          });
        } else {
          this.isActive = val;
          this.hideOptions();
        }
      }
    }
  };
</script>

<style>
  .select-container {
    font-family: var(--main-font-family);
    font-size: var(--main-font-size);
    user-select: none;
  }

  .select-container__select {
    position: relative;

    flex-grow: 1;

    height: 32px;
  }

  .select-control {
    white-space: nowrap;

    cursor: pointer;

    display: flex;

    justify-content: space-between;
    align-items: center;

    box-sizing: border-box;
    height: 32px;

    padding-left: 8px;
    padding-right: 8px;

    background: var(--background-color);

    border: 1px solid var(--border-color);

    border-radius: 5px;
  }

  .select-control__placeholder {
    font-size: var(--main-font-size);
    color: var(--gray-text-color);
  }

  .select-control__icon {
    color: var(--secondary-text-color);

    flex-shrink: 0;

    width: 18px;
    height: 18px;

    margin-left: 5px;

    & svg {
      transform: rotate(90deg);

      transition: transform ease-in-out var(--transition-duration);
    }
  }

  .select-control__icon_active svg {
    transform: rotate(270deg);
  }

  /* hack to override bootstrap styles */
  .select-container .select-control__input {
    font-family: var(--main-font-family);
    font-size: var(--main-font-size);

    padding: 0;

    margin: 0;

    border: none;

    &:focus {
      border: none;
      outline: none;
    }
  }

  .select-control__content {
    overflow: hidden;
    margin-left: 1px;
  }

  .select-container__label {
    & label {
      font-size: var(--main-font-size);
      font-weight: var(--medium-font-weight);

      color: var(--text-color);
      line-height: var(--main-line-height);

      white-space: nowrap;

      display: inline-block;

      padding: 0;

      margin: 0;
    }
  }

  .select-container_small {
    font-size: 12px;

    & .select-control__content {
      font-size: var(--secondary-font-size);
    }

    & .select-control,
    & .select-container__select {
      height: 28px;
    }
  }

  /* stylelint-disable-next-line plugin/stylelint-group-selectors */
  .select-container_large {
    font-size: 14px;

    & .select-control__content {
      font-size: var(--main-font-size);
    }

    & .select-control,
    & .select-container__select {
      height: 40px;
    }
  }

  .select-container_disabled {
    cursor: not-allowed;

    & .select-container__label label {
      color: var(--gray-text-color);
      cursor: not-allowed;
    }

    & .select-control {
      color: var(--gray-text-color);

      cursor: not-allowed;

      background: var(--gray-table-color);

      border: solid 1px var(--gray-table-color);
    }
  }

  .select-container_large-with-icon:not(.select-container_large-placeholder-only-name) {
    & .select-control {
      padding-left: 14px;
      padding-right: 14px;
    }

    & {
      .select-control, .select-container__select {
        height: 62px;
      }
    }

    &.select-container_with-bottom-text {
      .select-control, .select-container__select {
        height: 80px;
      }
    }
  }

  .select-container_label-top .select-container__label label {
    margin-bottom: 8px;
  }

  .select-container_label-left {
    display: flex;

    flex-shrink: 0;
    align-items: center;

    & .select-container__label label {
      margin-right: 8px;
    }
  }

  .select-control__popper_in-modal {
    z-index: calc(var(--popover-z-index) + 1);
  }

  .select-control__no-padding.select-items {
    border: none;
  }

  .select-control__no-height-limit.select-items {
    max-height: unset;
  }
</style>
